/**
 * Hook useWhatsAppStats - Phase 1: Dashboard Temps Réel
 * 
 * Récupère les statistiques temps réel des conversations WhatsApp.
 * Auto-refresh toutes les 30 secondes.
 * 
 * Usage:
 * ```tsx
 * const { stats, loading, error, refetch } = useWhatsAppStats()
 * 
 * <div>
 *   <p>Total: {stats?.total_conversations}</p>
 *   <p>En takeover: {stats?.in_takeover}</p>
 *   <p>Taux: {stats?.takeover_rate}%</p>
 * </div>
 * ```
 */
import { useState, useEffect, useCallback } from 'react'
import { getApiBase, getToken } from '../lib/utils'

export interface WhatsAppStats {
  total_conversations: number
  active_24h: number
  in_takeover: number
  in_auto: number
  takeover_rate: number
  timestamp: string
}

export function useWhatsAppStats(autoRefreshMs: number = 30000) {
  const [stats, setStats] = useState<WhatsAppStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const fetchStats = useCallback(async () => {
    try {
      const token = getToken()
      if (!token) {
        setError('No authentication token')
        return
      }
      
      const response = await fetch(`${getApiBase()}/whatsapp/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      
      const data = await response.json()
      setStats(data)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch WhatsApp stats:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])
  
  // Fetch initial
  useEffect(() => {
    fetchStats()
  }, [fetchStats])
  
  // Auto-refresh
  useEffect(() => {
    if (autoRefreshMs <= 0) return
    
    const interval = setInterval(fetchStats, autoRefreshMs)
    return () => clearInterval(interval)
  }, [fetchStats, autoRefreshMs])
  
  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  }
}
